import { z } from "zod";
import { isSupportedTokenChain, SUPPORTED_TOKEN_CHAINS } from "../caip10";
import { defineAd, withChoices } from "../core/ad-definition";
import { adlandAPI } from "../services/adland.api";

export const tokenAd = defineAd({
  type: "token",
  data: z.object({
    address: z.string().nonempty("Address is required"),
    chainId: withChoices(
      z
        .number()
        .int()
        .positive("Chain ID is required")
        .refine(
          isSupportedTokenChain,
          "Token ads are only supported on Ethereum, Base, Arbitrum and Robinhood",
        ),
      SUPPORTED_TOKEN_CHAINS.map((c) => ({
        label: `${c.label} (${c.chainId})`,
        value: c.chainId,
      })),
    ),
  }),
  metadata: z.object({
    name: z.string().optional(),
    symbol: z.string().optional(),
    decimals: z.number().optional(),
    logoURI: z.string().optional(),
  }),
  async verify({ address, chainId }) {
    const res = await adlandAPI.verify.token({ address, chainId });
    if (!res.verified) {
      throw new Error("Token verification failed");
    }
  },
  async getMetadata({ address, chainId }) {
    const token = await adlandAPI.metadata.token({ address, chainId });
    return {
      name: token.name,
      symbol: token.symbol,
      decimals: token.decimals,
      logoURI: token.logoURI,
    };
  },
});

export type TokenAdData = z.infer<typeof tokenAd.data>;
export type TokenAdMetadata = z.infer<NonNullable<typeof tokenAd.metadata>>;
export type TokenAd = {
  type: "token";
  data: TokenAdData;
  metadata: TokenAdMetadata;
};
